// src/components/EmailVerificationReminder.tsx
import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

type Props = {
  email: string;
  userId: string;
};

export default function EmailVerificationReminder({ email, userId }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');

  const REMINDER_KEY = `email_verify_reminder_${userId}`;
  const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds

  useEffect(() => {
    // Check if we should show the reminder
    const lastShown = localStorage.getItem(REMINDER_KEY);
    const now = Date.now();

    if (!lastShown || now - parseInt(lastShown) >= ONE_HOUR) {
      setIsVisible(true);
      localStorage.setItem(REMINDER_KEY, now.toString());
    }
  }, [REMINDER_KEY]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleResend = async () => {
    try {
      setResending(true);
      setMessage('');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setMessage('Please log in to resend verification email.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('resend-verification-email', {
        body: {
          email,
          userId,
          username: userData.user.user_metadata?.username || email.split('@')[0],
        },
      });

      if (error || !data?.success) {
        setMessage('Failed to send email. Please try again.');
        return;
      }

      setMessage('Verification email sent! Check your inbox.');
    } catch (e: any) {
      setMessage('Something went wrong. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl bg-theme-cards border border-theme-cards shadow-card overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-primary/20 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center shadow-glow-red">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-semibold text-white text-center mb-3">
            Verify your email
          </h3>

          {/* Description */}
          <p className="text-theme-text-secondary text-center mb-6 leading-relaxed">
            We've sent a verification link to{' '}
            <span className="font-medium text-white">{email}</span>. Please check your inbox and click the link to complete your account setup.
          </p>

          {/* Message */}
          {message && (
            <div className="mb-4 p-3 rounded-lg bg-theme-accent/10 border border-theme-accent/20 text-theme-accent text-sm text-center">
              {message}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleResend}
              disabled={resending}
              className="w-full px-6 py-3 rounded-xl bg-secondary text-white font-medium hover:bg-secondary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-glow-red"
            >
              {resending ? 'Sending...' : 'Resend verification email'}
            </button>

            <button
              onClick={handleClose}
              className="w-full px-6 py-3 rounded-xl bg-primary/40 text-white font-medium hover:bg-primary/50 transition-colors"
            >
              I'll do this later
            </button>
          </div>

          {/* Helper text */}
          <p className="mt-6 text-xs text-theme-text-secondary text-center">
            Didn't receive the email? Check your spam folder or try resending.
          </p>
        </div>
      </div>
    </div>
  );
}
